import { Radio } from '@base-ui/react/radio';
import { RadioGroup as BaseUIRadioGroup } from '@base-ui/react/radio-group';
import React, { forwardRef, useRef } from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import { Icon } from '../../assets/Icon/Icon';
import { outlineColors, outlines } from '../../common/outlines';
import { getVariantStyles, variantTokenBgColor } from '../../common/variant';
import { VisuallyHidden } from '../../common/visually_hidden';
import { Tooltip, useInsideTooltipTrigger } from '../../components/Tooltip/Tooltip';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { useControllableState } from '../../utilities/useControllableState';
import { selectors, transitions } from '../shared/styles';

type MultiToggleInputSizeProp = 'small' | 'large';

const multiToggleInputOptionSize = {
  small: {
    height: '$16',
    aspectRatio: '1 / 1',
  },
  large: {
    height: '$20',
    aspectRatio: '1 / 1',
  },
};

const MultiToggleInputTabIcon = styled(Icon);
MultiToggleInputTabIcon.displayName = 'MultiToggleInputTabIcon';

const MultiToggleInputTabTack = styled('div', {
  display: 'flex',
  backgroundColor: variantTokenBgColor,
  borderRadius: '99em',

  variants: {
    size: {
      small: {
        width: '$8',
        height: '$8',
      },
      large: {
        width: '$10',
        height: '$10',
      },
    },
    variant: {
      alternative: {
        ...getVariantStyles('alternative', false),
      },
      attention: {
        ...getVariantStyles('attention', false),
      },
      brand: {
        ...getVariantStyles('brand', false),
      },
      negative: {
        ...getVariantStyles('negative', false),
      },
      neutral: {
        ...getVariantStyles('neutral', false),
      },
      positive: {
        ...getVariantStyles('positive', false),
      },
    },
  },
});
MultiToggleInputTabTack.displayName = 'MultiToggleInputTabTack';

type MultiToggleInputTabContainerProps = {
  size?: MultiToggleInputSizeProp;
} & React.HTMLAttributes<HTMLDivElement>;

const MultiToggleInputTabContainer = styled('div', {
  position: 'absolute',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '99em',
  backgroundColor: colors.white,
  outline: outlines.control,
  outlineColor: outlineColors.control.raisedInitialLight,
  boxShadow: shadows.controlRaisedInitialLight,
  pointerEvents: 'none',
  transition: transitions.control,

  [selectors.hover]: {
    outlineColor: outlineColors.control.raisedHoveredLight,
    boxShadow: shadows.controlRaisedHoveredLight,
  },

  [darkThemeSelector]: {
    backgroundColor: colors.gray800,
    outlineColor: outlineColors.control.raisedInitialDark,
    boxShadow: shadows.controlRaisedInitialDark,

    [selectors.hover]: {
      outlineColor: outlineColors.control.raisedHoveredDark,
      boxShadow: shadows.controlRaisedHoveredDark,
    },
  },

  variants: {
    size: {
      ...multiToggleInputOptionSize,
    },
  },
}) as React.ComponentType<MultiToggleInputTabContainerProps>;

MultiToggleInputTabContainer.displayName = 'MultiToggleInputTabContainer';

type MultiToggleInputTabProps = {
  /**
   * The value of the option
   */
  icon?: IconName;
  /**
   * The size of the input
   */
  size?: MultiToggleInputSizeProp;
  /**
   * The variant of the icon
   */
  variant?: VariantProp;
} & Pick<React.HTMLAttributes<HTMLDivElement>, 'style' | 'className' | 'id'>;

function MultiToggleInputTab({
  icon,
  size = 'large',
  variant = 'neutral',
  ...remaining
}: MultiToggleInputTabProps) {
  return (
    <MultiToggleInputTabContainer aria-hidden="true" size={size} {...remaining}>
      {icon ? (
        <MultiToggleInputTabIcon icon={icon} size={size === 'small' ? 10 : 12} variant={variant} />
      ) : (
        <MultiToggleInputTabTack size={size} variant={variant} />
      )}
    </MultiToggleInputTabContainer>
  );
}

const MultiToggleInputOptionLabel = styled(Text, {
  userSelect: 'none',
  variants: {
    size: {
      small: {
        fontSize: '$12',
        lineHeight: '$16',
      },
      large: {
        fontSize: '$14',
        lineHeight: '$20',
      },
    },
  },
});
MultiToggleInputOptionLabel.displayName = 'MultiToggleInputOptionLabel';

const MultiToggleInputOptionTack = styled('div', {
  backgroundColor: colors.gray200,
  borderRadius: '99em',

  [darkThemeSelector]: {
    backgroundColor: colors.gray600,
  },

  variants: {
    size: {
      small: {
        heightAll: '$8',
        aspectRatio: '1 / 1',
      },
      large: {
        heightAll: '$10',
        aspectRatio: '1 / 1',
      },
    },
  },
});
MultiToggleInputOptionTack.displayName = 'MultiToggleInputOptionTack';

const MultiToggleInputOptionContainer = styled('span', {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    isDisabled: {
      true: {
        opacity: 0.5,
        cursor: 'not-allowed',
      },
      false: {},
    },
    size: {
      ...multiToggleInputOptionSize,
    },
  },
});

MultiToggleInputOptionContainer.displayName = 'MultiToggleInputOptionContainer';

export type MultiToggleInputOptionProps = {
  /**
   * Set if the option is disabled
   */
  disabled?: boolean;
  /**
   * The label to display for the option
   */
  label: React.ReactNode;
  /**
   * Optional tooltip content shown on hover. Falls back to `label` if not provided.
   */
  tooltip?: React.ReactNode;
  /**
   * Optional icon to display for the option
   */
  icon?: IconName;
  /**
   * The size of the option
   */
  size?: MultiToggleInputSizeProp;
  /**
   * The value of the option
   */
  value: string;
  /**
   * The color variant for the option
   */
  variant?: VariantProp;
};

function MultiToggleInputOption({
  disabled,
  icon,
  label,
  tooltip,
  size,
  value,
  variant,
  onClick,
  ...remaining
}: MultiToggleInputOptionProps & { onClick?: React.MouseEventHandler<HTMLElement> }) {
  const insideTooltipTrigger = useInsideTooltipTrigger();

  const contents = (
    <>
      <MultiToggleInputOptionTack size={size} />
      {/* The tack is a decorative dot, so this is the only thing naming the option.
          The label is otherwise visible only in the tooltip, which is not announced. */}
      <VisuallyHidden>{label}</VisuallyHidden>
    </>
  );

  // Disabled options drop radio semantics entirely: axe's nested-interactive rule rejects
  // elements with interactive roles inside the trigger button a disabled-reason
  // `<Tooltip asChild={false}>` wrapper renders, even with a negative tabindex.
  if (disabled) {
    return (
      <MultiToggleInputOptionContainer isDisabled size={size} {...remaining}>
        {contents}
      </MultiToggleInputOptionContainer>
    );
  }

  const option = (
    <Radio.Root
      onClick={onClick}
      render={<MultiToggleInputOptionContainer size={size} {...remaining} />}
      value={value}
    >
      {contents}
    </Radio.Root>
  );

  if (insideTooltipTrigger) {
    return option;
  }

  return (
    <Tooltip
      delay={500}
      contents={
        <MultiToggleInputOptionLabel size={size}>{tooltip ?? label}</MultiToggleInputOptionLabel>
      }
    >
      {option}
    </Tooltip>
  );
}

const MultiToggleInputTrack = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: colors.gray100,
  borderRadius: '99em',
  cursor: 'pointer',
  transition: transitions.control,

  [selectors.focus]: {
    boxShadow: shadows.focusRingLight,
  },

  [darkThemeSelector]: {
    backgroundColor: colors.gray700,

    [selectors.focus]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    isDisabled: {
      true: {
        opacity: 0.5,
        cursor: 'not-allowed',
      },
      false: {},
    },
    size: {
      small: {
        height: '$16',
      },
      large: {
        height: '$20',
      },
    },
  },
});
MultiToggleInputTrack.displayName = 'MultiToggleInputTrack';

const MultiToggleInputEnds = styled(Text, {
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',

  variants: {
    // Rendered `as="button"`, so this is named `isDisabled` — Stitches consumes a variant
    // named `disabled` and it would never reach the element.
    isDisabled: {
      true: {
        opacity: 0.5,
        cursor: 'not-allowed',
      },
      false: {},
    },
    size: {
      small: {
        ...SmallSansSizes,
      },
      large: {
        ...BodySansSizes,
      },
    },
  },
});
MultiToggleInputEnds.displayName = 'MultiToggleInputEnds';

const MultiToggleInputContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: '$6',
});
MultiToggleInputContainer.displayName = 'MultiToggleInputContainer';

export type MultiToggleInputProps = {
  /**
   * The accessible label for the toggle group
   */
  'aria-label'?: string;
  /**
   * The id of an element labelling the toggle group
   */
  'aria-labelledby'?: string;
  /**
   * The initially selected value when uncontrolled
   */
  defaultValue?: string;
  /**
   * Set if the component is disabled
   */
  disabled?: boolean;
  /**
   * The name of the underlying form control
   */
  name?: string;
  /**
   * Callback when selection changes
   */
  onChange?: (value: string) => void;
  /**
   * The options to display in the toggle
   */
  options: MultiToggleInputOptionProps[];
  /**
   * Set the size utilizing the controlSize API
   */
  size?: MultiToggleInputSizeProp;
  /**
   * The currently selected value
   */
  value?: string;
};

export function MultiToggleInputInner(
  {
    size = 'large',
    options,
    value,
    defaultValue,
    onChange,
    disabled = false,
    name,
    ...remaining
  }: MultiToggleInputProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const previousValueRef = useRef<string | undefined>(value);
  const [selectedValue, setSelectedValue] = useControllableState<string | null>({
    defaultValue: defaultValue ?? null,
    onChange: (next) => {
      if (next !== null) onChange?.(next);
    },
    value,
  });

  const selectedIndex = options.findIndex((opt) => opt.value === selectedValue);
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;

  const isSelectable = (optionValue: string) =>
    !disabled && !options.find((opt) => opt.value === optionValue)?.disabled;

  const handleValueChange = (newValue: string) => {
    if (newValue === selectedValue) {
      const previousValue = previousValueRef.current || options[1].value;
      if (isSelectable(previousValue)) setSelectedValue(previousValue);
    } else if (isSelectable(newValue)) {
      previousValueRef.current = selectedValue || undefined;
      setSelectedValue(newValue);
    }
  };

  const trackContents = (
    <>
      {selectedOption && (
        <MultiToggleInputTab
          {...selectedOption}
          size={size}
          variant={selectedOption.variant}
          style={{
            left: `calc(${(100 / options.length) * selectedIndex}%)`,
          }}
        />
      )}
      {options.map((option) => (
        <MultiToggleInputOption
          key={option.value}
          {...option}
          disabled={disabled || option.disabled}
          size={size}
          variant={option.variant}
          onClick={() => {
            // base-ui only reports changes, so re-selecting the current option has to be
            // caught here for the toggle-back-to-previous behavior.
            if (option.value === selectedValue) {
              handleValueChange(option.value);
            }
          }}
        />
      ))}
    </>
  );

  return (
    <MultiToggleInputContainer>
      <MultiToggleInputEnds
        tabIndex={-1}
        as="button"
        type="button"
        context="body"
        disabled={disabled || options[0].disabled}
        isDisabled={disabled || options[0].disabled}
        size={size}
        variant={options[0] === selectedOption ? options[0].variant : 'neutral'}
        onClick={() => handleValueChange(options[0].value)}
      >
        {options[0].label}
      </MultiToggleInputEnds>
      {disabled ? (
        // Same inert-when-disabled reasoning as the options: no radiogroup role, no inputs.
        <MultiToggleInputTrack ref={ref} isDisabled size={size}>
          {trackContents}
        </MultiToggleInputTrack>
      ) : (
        <BaseUIRadioGroup
          {...remaining}
          ref={ref}
          name={name}
          onValueChange={(next) => {
            if (typeof next === 'string') handleValueChange(next);
          }}
          render={<MultiToggleInputTrack size={size} />}
          value={selectedValue}
        >
          {trackContents}
        </BaseUIRadioGroup>
      )}
      <MultiToggleInputEnds
        tabIndex={-1}
        as="button"
        type="button"
        context="body"
        disabled={disabled || options[options.length - 1].disabled}
        isDisabled={disabled || options[options.length - 1].disabled}
        size={size}
        variant={
          options[options.length - 1] === selectedOption
            ? options[options.length - 1].variant
            : 'neutral'
        }
        onClick={() => handleValueChange(options[options.length - 1].value)}
      >
        {options[options.length - 1].label}
      </MultiToggleInputEnds>
    </MultiToggleInputContainer>
  );
}

export const MultiToggleInput = forwardRef(MultiToggleInputInner);
